/**
 * RouteTransition — Apple-quiet route fade.
 *
 * Wraps every route element with a 180ms ease-out fade + 4px translate-up,
 * keyed on pathname so React unmounts old/mounts new cleanly. Respects the
 * user's reduced-motion preference (renders without animation).
 *
 * Uses framer-motion to keep the implementation small and accessible — no
 * springs, no bounce. The brief is "feels considered, not flashy".
 */

import { motion, useReducedMotion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

export function RouteTransition({
  children,
}: {
  children: React.ReactNode;
}) {
  const { pathname } = useLocation();
  const reduceMotion = useReducedMotion();

  if (reduceMotion) return <>{children}</>;

  return (
    <motion.div
      key={pathname}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: [0.25, 1, 0.5, 1] }}
    >
      {children}
    </motion.div>
  );
}
